import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  employee_no: "", full_name: "", ic_number: "", email: "", phone: "",
  department: "", position: "", employment_status: "active", date_joined: "",
  date_of_birth: "", gender: "male", nationality: "Malaysian", marital_status: "single",
  epf_number: "", socso_number: "", eis_number: "", tax_number: "",
  bank_name: "", bank_account: "", basic_salary: "", allowances: [],
  working_mode: "", shift_type: "", working_hours_per_day: "",
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls = "border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500";

export const EmployeeDialog = ({ open, onOpenChange, employee, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!employee;

  useEffect(() => {
    if (employee) setForm({ ...EMPTY, ...employee, allowances: employee.allowances || [],
      working_mode: employee.working_mode || "", shift_type: employee.shift_type || "",
      working_hours_per_day: employee.working_hours_per_day ?? "" });
    else setForm(EMPTY);
  }, [employee, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAllowance = (i, k, v) =>
    setForm((f) => ({ ...f, allowances: f.allowances.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) }));
  const addAllowance = () => setForm((f) => ({ ...f, allowances: [...f.allowances, { name: "", amount: 0 }] }));
  const removeAllowance = (i) => setForm((f) => ({ ...f, allowances: f.allowances.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.employee_no || !form.full_name) {
      toast.error("Employee number and full name are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      basic_salary: parseFloat(form.basic_salary) || 0,
      working_hours_per_day: parseFloat(form.working_hours_per_day) || null,
      shift_type: form.working_mode === "shift" ? form.shift_type : "",
      allowances: form.allowances
        .filter((a) => a.name)
        .map((a) => ({ name: a.name, amount: parseFloat(a.amount) || 0 })),
    };
    try {
      if (editing) await api.put(`/employees/${employee.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {editing ? "Edit Employee" : "Add Employee"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-500">Personal</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Employee No *">
                <Input data-testid="emp-employee-no" value={form.employee_no} onChange={(e) => set("employee_no", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Full Name *">
                <Input data-testid="emp-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
              </Field>
              <Field label="IC / MyKad No">
                <Input data-testid="emp-ic" value={form.ic_number} onChange={(e) => set("ic_number", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Email">
                <Input data-testid="emp-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date of Birth">
                <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Marital Status">
                <Select value={form.marital_status} onValueChange={(v) => set("marital_status", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-500">Employment</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Department">
                <Input data-testid="emp-department" value={form.department} onChange={(e) => set("department", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Position">
                <Input value={form.position} onChange={(e) => set("position", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Date Joined">
                <Input type="date" value={form.date_joined} onChange={(e) => set("date_joined", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Status">
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on-leave">On Leave</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Basic Salary (RM)">
                <Input data-testid="emp-salary" type="number" value={form.basic_salary} onChange={(e) => set("basic_salary", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Nationality">
                <Input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-500">Work Schedule</p>
            <p className="-mt-2 mb-3 text-xs text-[#9CA3AF]">Drives the OT hourly rate (gross ÷ working days ÷ hours). Leave as "Company default" to follow Settings.</p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Working Mode">
                <select data-testid="emp-working-mode" value={form.working_mode} onChange={(e) => set("working_mode", e.target.value)} className="h-9 w-full rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-2 text-sm text-[#0F172A]">
                  <option value="">Company default</option>
                  <option value="normal">Normal</option>
                  <option value="shift">Shift</option>
                </select>
              </Field>
              <Field label="Shift Type">
                <select data-testid="emp-shift-type" disabled={form.working_mode !== "shift"} value={form.shift_type} onChange={(e) => set("shift_type", e.target.value)} className="h-9 w-full rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-2 text-sm text-[#0F172A] disabled:opacity-50">
                  <option value="">Company default</option>
                  <option value="2">2-shift</option>
                  <option value="3">3-shift</option>
                </select>
              </Field>
              <Field label="Working Hours / Day">
                <Input data-testid="emp-working-hours" type="number" step="0.5" placeholder="Company default" value={form.working_hours_per_day} onChange={(e) => set("working_hours_per_day", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-500">Statutory & Bank</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="EPF No"><Input value={form.epf_number} onChange={(e) => set("epf_number", e.target.value)} className={inputCls} /></Field>
              <Field label="SOCSO No"><Input value={form.socso_number} onChange={(e) => set("socso_number", e.target.value)} className={inputCls} /></Field>
              <Field label="EIS No"><Input value={form.eis_number} onChange={(e) => set("eis_number", e.target.value)} className={inputCls} /></Field>
              <Field label="Tax / LHDN No"><Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)} className={inputCls} /></Field>
              <Field label="Bank Name"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className={inputCls} /></Field>
              <Field label="Bank Account No"><Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} className={inputCls} /></Field>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-500">Allowances</p>
              <Button type="button" variant="ghost" size="sm" data-testid="add-allowance" onClick={addAllowance} className="h-7 text-red-500 hover:bg-red-500/10">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {form.allowances.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Name" value={a.name} onChange={(e) => setAllowance(i, "name", e.target.value)} className={inputCls} />
                  <Input placeholder="Amount" type="number" value={a.amount} onChange={(e) => setAllowance(i, "amount", e.target.value)} className={`${inputCls} w-32`} />
                  <button type="button" onClick={() => removeAllowance(i)} className="rounded-md p-2 text-[#6B7280] hover:text-red-400">
                    <Trash size={16} />
                  </button>
                </div>
              ))}
              {form.allowances.length === 0 && <p className="text-sm text-[#6B7280]">No allowances added.</p>}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">
            Cancel
          </Button>
          <Button data-testid="save-employee" onClick={submit} disabled={saving} className="bg-red-500 font-semibold text-white hover:bg-red-600">
            {saving ? "Saving…" : editing ? "Update" : "Add Employee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
